import {
  DreamerStackBarChartDataType,
  StackSegmentConfig
} from "@/components/DreamerCharts/types";
import { CHART_SEGMENT_COLORS } from "@/components/DreamerCharts/chartColors";
import {
  DreamerChartAxisLabels,
  DreamerTableRow
} from "@/entities/attachments/types";

interface BuildStackedChartDataResult {
  data: DreamerStackBarChartDataType[];
  segments: StackSegmentConfig[];
}

interface BuildStackedChartDataParams {
  rows: DreamerTableRow[];
  axisLabels: DreamerChartAxisLabels | null;
  formatLabel: (raw: unknown) => string;
}

export const buildStackedChartData = ({
  rows,
  axisLabels,
  formatLabel
}: BuildStackedChartDataParams): BuildStackedChartDataResult => {
  if (!axisLabels?.x || !axisLabels?.y || !axisLabels?.legend) {
    return { data: [], segments: [] };
  }

  const xAxisKey = axisLabels.x;
  const yAxisKey = axisLabels.y;
  const legendAxisKey = axisLabels.legend;

  const legendValues: string[] = [];
  const seenLegends = new Set<string>();
  // Map raw legend text to a stable internal data key. Using the raw legend
  // as the object key risks colliding with reserved fields like `label`,
  // which would corrupt the x-axis label and break Recharts stacking.
  const legendToDataKey = new Map<string, string>();
  const xValueOrder: string[] = [];
  const xValueIndexMap = new Map<string, DreamerStackBarChartDataType>();

  rows.forEach((row) => {
    // Skip rows missing a legend value rather than collapsing them into a
    // blank-labelled segment — mirrors buildLineChartData's behaviour.
    const rawLegendValue = row[legendAxisKey];
    if (rawLegendValue === null || rawLegendValue === undefined) return;
    const legend = String(rawLegendValue);
    if (legend.trim() === "") return;

    if (!seenLegends.has(legend)) {
      seenLegends.add(legend);
      legendValues.push(legend);
      legendToDataKey.set(legend, `segment_${legendValues.length - 1}`);
    }
    const dataKey = legendToDataKey.get(legend)!;

    const xLabel = formatLabel(row[xAxisKey]);
    let entry = xValueIndexMap.get(xLabel);
    if (!entry) {
      entry = { label: xLabel };
      xValueIndexMap.set(xLabel, entry);
      xValueOrder.push(xLabel);
    }

    const rawY = row[yAxisKey];
    const numericY = typeof rawY === "number" ? rawY : Number(rawY);
    const safeY = Number.isFinite(numericY) ? numericY : 0;
    const previous = entry[dataKey];
    entry[dataKey] = (typeof previous === "number" ? previous : 0) + safeY;
  });

  xValueOrder.forEach((xLabel) => {
    const entry = xValueIndexMap.get(xLabel)!;
    legendValues.forEach((legend) => {
      const dataKey = legendToDataKey.get(legend)!;
      if (typeof entry[dataKey] !== "number") {
        entry[dataKey] = 0;
      }
    });
  });

  const segments: StackSegmentConfig[] = legendValues.map((legend, index) => ({
    dataKey: legendToDataKey.get(legend)!,
    label: legend,
    color: CHART_SEGMENT_COLORS[index % CHART_SEGMENT_COLORS.length]
  }));

  return {
    data: xValueOrder.map(
      (xLabel) => xValueIndexMap.get(xLabel) as DreamerStackBarChartDataType
    ),
    segments
  };
};
